/**
 * AdminAuthContext
 * Manages admin authentication state across all admin pages
 * Uses synchronous localStorage reads for immediate auth state
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

// Helper to safely get from localStorage
const getStoredToken = () => {
  try {
    return localStorage.getItem('admin_session_token') || null;
  } catch (e) {
    return null;
  }
};

const getStoredUser = () => {
  try {
    const stored = localStorage.getItem('admin_user');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

export const AdminAuthProvider = ({ children }) => {
  // Initialize from localStorage synchronously
  const [adminToken, setAdminToken] = useState(getStoredToken);
  const [adminUser, setAdminUser] = useState(getStoredUser);
  const navigate = useNavigate();

  // Login function - called from AdminLoginPage
  const login = useCallback((token, user) => {
    localStorage.setItem('admin_session_token', token);
    localStorage.setItem('admin_user', JSON.stringify(user));
    setAdminToken(token);
    setAdminUser(user);
  }, []);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('admin_session_token');
    localStorage.removeItem('admin_user');
    setAdminToken(null);
    setAdminUser(null);
    navigate('/admin/login');
  }, [navigate]);

  // Check if authenticated - also check localStorage as a fallback for any race conditions
  const isAuthenticated = !!adminToken || !!getStoredToken();

  // Get auth config for axios requests - get token fresh from localStorage as fallback
  const getAuthConfig = useCallback(() => {
    const token = adminToken || getStoredToken();
    return { headers: { 'Authorization': `Bearer ${token}` } };
  }, [adminToken]);

  // Handle 401 errors - logout and redirect
  const handleAuthError = useCallback(() => {
    logout();
  }, [logout]);

  const value = {
    adminToken,
    adminUser,
    isAuthenticated,
    isLoading: false, // No async loading - synchronous from localStorage
    login,
    logout,
    getAuthConfig,
    handleAuthError
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
